import { toHTML } from "@portabletext/to-html";
import DineroFactory from "dinero.js";
import {
  CartProps,
  CartTableProps,
  FreeShippingBarProps,
  OrderSummaryProps,
} from "@bluebottlecoffee/design-system";
import {
  LineItemsProps,
  PromoFormProps,
  GiftMessageFormProps,
} from "@bluebottlecoffee/design-system/dist/OrderSummary/components";
import { ProductRecsProps } from "@bluebottlecoffee/design-system/dist/ProductRecs/ProductRecs";
import { Money, Product } from "@bluebottlecoffee/design-system/dist/lib/types";
import { GiftCardFormCopy } from "@bluebottlecoffee/design-system/dist/GiftCard/GiftCardForm";
import { productPage } from "../link-builders";
import {
  Cart as CartSchema,
  FreeShippingBar as FreeShippingBarSchema,
  GiftMessageCopy as GiftMessageCopySchema,
  LineItems as LineItemsSchema,
  OrderSummary as OrderSummarySchema,
  PromoCopy as PromoCopySchema,
} from "../sanity-schema";
import { toLinkProps } from "./link";

export function toFreeShippingBarProps(
  data: FreeShippingBarSchema,
  lang: string,
): Omit<FreeShippingBarProps, "estimatedTotal"> {
  return {
    aboveThresholdText: data.aboveThresholdText[lang],
    belowThresholdText: data.belowThresholdText[lang],
    freeShipThreshold: data.freeShipThreshold as Money,
  };
}

export function toLineItemsProps(
  data: LineItemsSchema,
  lang: string,
): Omit<LineItemsProps, "grossSubtotal" | "currency"> {
  return {
    creditLabel: data.creditLabel[lang],
    discountLabel: data.discountLabel[lang],
    estimatedTotalLabel: data.estimatedTotalLabel[lang],
    free: data.free[lang],
    freeShippingText: data.freeShippingText[lang],
    grossSubtotalLabel: data.grossSubtotalLabel[lang],
    item: data.item[lang],
    shippingLabel: data.shippingLabel[lang],
    taxesLabel: data.taxesLabel[lang],
    tbd: data.tbd[lang],
  };
}

export function toGiftMessageFormProps(
  data: GiftMessageCopySchema,
  lang: string,
): Omit<GiftMessageFormProps, "addGiftMessage" | "removeGiftMessage"> {
  return {
    copy: {
      add: data.add[lang],
      cancel: data.cancel[lang],
      to: data.to[lang],
      title: data.title[lang],
      from: data.from[lang],
      message: data.message[lang],
      saveMessageButton: data.saveMessageButton[lang],
      removeMessageButton: data.removeMessageButton[lang],
      fromValidationMessage: data.fromValidationMessage[lang],
      toValidationMessage: data.toValidationMessage[lang],
      giftNoteValidationMessage: data.giftNoteValidationMessage[lang],
    },
    removeGiftMessageAlert: {
      remove: data.removeGiftMessageAlert.remove[lang],
      verify: data.removeGiftMessageAlert.verify[lang],
      yes: data.removeGiftMessageAlert.yes[lang],
      no: data.removeGiftMessageAlert.no[lang],
    },
    giftMessageCartCopy: {
      from: data.from[lang],
      to: data.to[lang],
      message: data.message[lang],
    },
  };
}

export function toPromoFormCopyProps(
  data: PromoCopySchema,
  lang: string,
): Pick<PromoFormProps, "promoCopy"> {
  return {
    promoCopy: {
      add: data.add[lang],
      apply: data.apply[lang],
      placeholder: data.placeholder[lang],
    },
  };
}
export function toRemovePromoAlertProps(
  data: PromoCopySchema,
  lang: string,
): Pick<PromoFormProps, "removePromoAlert"> {
  return {
    removePromoAlert: {
      remove: data.removePromoAlert.remove[lang],
      verify: data.removePromoAlert.verify[lang],
      yes: data.removePromoAlert.yes[lang],
      no: data.removePromoAlert.no[lang],
    },
  };
}
export function toPromoFormProps(
  data: PromoCopySchema,
  lang: string,
): Omit<PromoFormProps, "removePromoCode" | "addPromoCode"> {
  return {
    ...toPromoFormCopyProps(data, lang),
    ...toRemovePromoAlertProps(data, lang),
  };
}

type OrderSummarySchemaProps = Omit<
  OrderSummaryProps,
  "lineItems" | "promoForm" | "ctaClick" | "subsPresent" | "giftMessageForm"
> & {
  giftMessageForm: Omit<
    GiftMessageFormProps,
    "addGiftMessage" | "removeGiftMessage"
  >;
  lineItems: Omit<LineItemsProps, "grossSubtotal" | "currency">;
  promoForm: Omit<PromoFormProps, "removePromoCode" | "addPromoCode">;
};
export function toOrderSummaryProps(
  data: OrderSummarySchema,
  lang: string,
): OrderSummarySchemaProps {
  return {
    lineItems: toLineItemsProps(data.lineItems, lang),
    promoForm: toPromoFormProps(data.promoCopy, lang),
    giftMessageForm: toGiftMessageFormProps(data.giftMessageCopy, lang),
    subsAgreement: {
      text: data.subsAgreement.subsAgreement[lang],
      title: data.subsAgreement.title[lang],
      onChange: () => {},
    },
    cta: {
      text: data.cta[lang],
    },
    heading: data.heading[lang],
  };
}

export function toCartTableProps(
  data: CartSchema,
  lang: string,
  giftCardFormCopy: GiftCardFormCopy,
): Omit<
  CartTableProps,
  | "cartLineItems"
  | "removeFromCart"
  | "modifyQuantity"
  | "modifyGiftCards"
  | "emailIsValid"
> {
  return {
    giftCardFormCopy,
    removeItemButtonLabel: data.removeItemButtonLabel[lang],
    subscriptionBenefitsCopy: toHTML(data.subscriptionBenefitsCopy[lang]),
    tableHeadings: {
      item: data.tableHeaders.item[lang],
      detail: data.tableHeaders.detail[lang],
      price: data.tableHeaders.price[lang],
      quantity: data.tableHeaders.quantity[lang],
    },
  };
}

export function toProductRecsProps(
  data,
  region: string,
  lang: string,
  currency: DineroFactory.Currency,
): Product[] {
  return data.map((rec) => ({
    description: rec.description[lang],
    images: rec.images.map((image) => ({
      desktop: {
        altText: image.desktop.altText[lang],
        src:
          `/${image.desktop?.source.public_id}.${image.desktop?.source.format}` ||
          "v1612883439/blue_bottle_brand_assets/Bottle_Logo-Small.001_031820.png",
      },
      mobile: {
        altText: image.mobile?.altText[lang],
        src:
          `/${image.mobile?.source.public_id}.${image.mobile?.source.format}` ||
          `/${image.desktop?.source.public_id}.${image.desktop?.source.format}` ||
          "v1612883439/blue_bottle_brand_assets/Bottle_Logo-Small.001_031820.png",
      },
    })),
    name: rec.name[lang],
    slug: productPage({
      slug: rec.slug.current,
      ...{ region, lang },
    }),
    subscription: rec.subscription,
    subscriptionOptions: rec.subscriptionOptions?.map((option) => ({
      item: option.item,
      quantityLabel: option.quantityLabel,
    })),
    variants: rec.variants.map((variant) => ({
      id: parseInt(variant._id.replace("variant_", ""), 10),
      cartLimit: variant.cartLimit,
      images:
        variant.images?.map((image) => ({
          desktop: {
            altText: image.desktop.altText[lang],
            src:
              `/${image.desktop?.source.public_id}.${image.desktop?.source.format}` ||
              "v1612883439/blue_bottle_brand_assets/Bottle_Logo-Small.001_031820.png",
          },
          mobile: {
            altText: image.mobile?.altText[lang],
            src:
              `/${image.mobile?.source.public_id}.${image.mobile?.source.format}` ||
              `/${image.desktop?.source.public_id}.${image.desktop?.source.format}` ||
              "v1612883439/blue_bottle_brand_assets/Bottle_Logo-Small.001_031820.png",
          },
        })) ?? [],
      initialInventoryCount: variant.initialInventoryCount,
      price: {
        amount: variant.price,
        currency: currency ?? "USD", // TODO: determine a smarter default
      },
      optionValues: variant.optionValues.map((value) => ({
        presentation: value.presentation,
      })),
      regularPrice: {
        amount: variant.regularPrice,
        currency: currency ?? "USD", // TODO: determine a smarter default
      },
      sku: variant.sku,
    })),
  }));
}

export function toProductRecsCopyProps(
  data: CartSchema,
  lang: string,
): Omit<
  ProductRecsProps,
  | "addToCartClick"
  | "cartView"
  | "emptyCart"
  | "productRecs"
  | "productRecShopCards"
> {
  return {
    productRecsCopy: {
      desktop: {
        buttonLabel: data.productRecsCopy.desktop.buttonLabel[lang],
        heading: data.productRecsCopy.desktop.heading[lang],
      },
      mobile: {
        buttonLabel: data.productRecsCopy.mobile.buttonLabel[lang],
        heading: data.productRecsCopy.mobile.heading[lang],
      },
      emptyCart: {
        buttonLabel: data.productRecsCopy.emptyCart.buttonLabel[lang],
        heading: data.productRecsCopy.emptyCart.heading[lang],
      },
      tableHeadings: {
        item: data.productRecsCopy.tableHeadings.item[lang],
        price: data.productRecsCopy.tableHeadings.price[lang],
        variant: data.productRecsCopy.tableHeadings.option[lang],
        quantity: data.productRecsCopy.tableHeadings.quantity[lang],
        addToCart: data.productRecsCopy.tableHeadings.addToCart[lang],
      },
    },
  };
}

export type CartSchemaProps = Omit<
  CartProps,
  | "freeShippingBarProps"
  | "orderSummaryProps"
  | "cartTableProps"
  | "productRecsProps"
> & {
  freeShippingBarProps: Omit<FreeShippingBarProps, "estimatedTotal">;
  orderSummaryProps: OrderSummarySchemaProps;
  cartTableProps: Omit<
    CartTableProps,
    | "cartLineItems"
    | "removeFromCart"
    | "modifyQuantity"
    | "modifyGiftCards"
    | "emailIsValid"
  >;
  productRecsProps: Omit<
    ProductRecsProps,
    "addToCartClick" | "emptyCart" | "productRecs" | "productRecShopCards"
  >;
};
export function toCartProps(
  data: CartSchema,
  giftCardFormCopy: GiftCardFormCopy,
  region: string,
  lang: string,
): CartSchemaProps {
  return {
    emptyCartCopy: data.emptyCartCopy[lang],
    cartTableProps: toCartTableProps(data, lang, giftCardFormCopy),
    freeShippingBarProps: toFreeShippingBarProps(data.freeShippingBar, lang),
    orderSummaryProps: toOrderSummaryProps(data.orderSummary, lang),
    productRecsProps: toProductRecsCopyProps(data, lang),
    returnToShopLink: toLinkProps(data.returnToShopLink, region, lang),
  };
}
